import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { ApiInternal } from "src/api/api_internal";
import { useTranslation } from "react-i18next";
import { Badge } from "src/components/base/badge";
import React from "react";
import { BackButton, Button, PrimaryButton } from "src/components/base/button";
import { Tab, TabMenu } from "src/components/base/tab-menu";
import SINGLE_PLUGIN_CONTEXT from "src/context/internal/plugin";
import { Heading } from "src/components/base/heading";
import { TagIcon } from "@heroicons/react/20/solid";
import { Text } from "src/components/base/text";
import { Dialog, DialogActions, DialogDescription, DialogTitle } from "src/components/base/dialog";
import Outlet from "src/components/base/outlet";

export const Route = createFileRoute("/_admin/a/plugins/$pluginId/_plugin")({
    component: RouteComponent,
    loader: async ({ params }) => await ApiInternal.plugins.getSingle(params.pluginId),
});

/** Base layout for a single plugin's views */
function RouteComponent() {
    const [tg] = useTranslation();
    const [t] = useTranslation("admin-plugin");

    const router = useRouter();
    const navigate = useNavigate();
    const plugin = Route.useLoaderData();

    const [openUpdatePlugin, setOpenUpdatePlugin] = React.useState(false);
    const [openDeletePlugin, setOpenDeletePlugin] = React.useState(false);

    const updatePlugin = async () => {
        await ApiInternal.plugins.update(plugin.uuid);
        await router.invalidate();
        setOpenUpdatePlugin(false);
    };

    const deletePlugin = async () => {
        await ApiInternal.plugins.delete(plugin.uuid);
        if (plugin.store) {
            await router.invalidate();
        } else {
            await navigate({
                to: "/a/plugins",
                search: { page: 1, search: "", filter: "all" },
            });
        }
        setOpenDeletePlugin(false);
    };

    return (
        <div className={"flex flex-col gap-6"}>
            <BackButton className={"self-start"} href={"/a/plugins"}>
                {t("button.back-to-plugin-overview")}
            </BackButton>

            <div className={"flex w-full flex-wrap items-start justify-between gap-4 pb-6"}>
                <img
                    className={"size-32"}
                    alt={t("label.plugin-image")}
                    src={`data:image/png;base64,${plugin.image}`}
                />
                <div className={"flex grow flex-col gap-3"}>
                    <Heading>{t("heading.plugin", { plugin: plugin.name })}</Heading>

                    <div className={"flex gap-1"}>
                        {plugin.installed && <Badge color={"cyan"}>{t("label.installed")}</Badge>}
                        {plugin.outdated && (
                            <Badge color={"orange"} title={t("description.outdated")}>
                                {t("label.outdated")}
                            </Badge>
                        )}
                        {!plugin.store && (
                            <Badge color={"zinc"} title={t("description.local")}>
                                {t("label.local")}
                            </Badge>
                        )}
                        {plugin.installed && (
                            <Badge>
                                <TagIcon className={"size-4"} />
                                {plugin.version}
                            </Badge>
                        )}
                        {plugin.store && (!plugin.installed || plugin.store.current_version !== plugin.version) && (
                            <Badge>
                                <TagIcon className={"size-4"} />
                                {plugin.store.current_version}
                            </Badge>
                        )}
                    </div>

                    <Text>{plugin.description}</Text>
                </div>
                <div className={"flex flex-col gap-3"}>
                    {!plugin.installed && (
                        <PrimaryButton
                            disabled={!plugin.store}
                            onClick={async () => {
                                await ApiInternal.plugins.install(plugin.uuid);
                                await router.invalidate();
                            }}
                        >
                            {t("button.install")}
                        </PrimaryButton>
                    )}
                    {plugin.outdated && (
                        <PrimaryButton onClick={async () => setOpenUpdatePlugin(true)}>
                            {t("button.update-plugin")}
                        </PrimaryButton>
                    )}
                    {plugin.installed && (
                        <Button color={"red"} onClick={async () => setOpenDeletePlugin(true)}>
                            {t("button.delete-plugin")}
                        </Button>
                    )}
                </div>
            </div>

            <TabMenu>
                <Tab href={"/a/plugins/$pluginId/rulesets"} params={{ pluginId: plugin.uuid }}>
                    {t("label.rulesets")}
                </Tab>
            </TabMenu>

            <div className={"mt-6"}>
                <SINGLE_PLUGIN_CONTEXT.Provider
                    value={{
                        plugin,
                        reset: async () => {
                            await router.invalidate();
                        },
                    }}
                >
                    <Outlet />
                </SINGLE_PLUGIN_CONTEXT.Provider>
            </div>

            <Dialog open={openUpdatePlugin} onClose={setOpenUpdatePlugin}>
                <DialogTitle>{t("heading.update-plugin", { plugin: plugin.name })}</DialogTitle>
                <DialogDescription>{t("description.update-plugin")}</DialogDescription>
                <DialogActions>
                    <Button onClick={() => setOpenUpdatePlugin(false)}>{tg("button.cancel")}</Button>
                    <PrimaryButton onClick={async () => await updatePlugin()}>{t("button.update")}</PrimaryButton>
                </DialogActions>
            </Dialog>

            <Dialog open={openDeletePlugin} onClose={setOpenDeletePlugin}>
                <DialogTitle>{t("heading.delete-plugin", { plugin: plugin.name })}</DialogTitle>
                <DialogDescription>{t("description.delete-plugin")}</DialogDescription>
                <DialogActions>
                    <Button onClick={() => setOpenDeletePlugin(false)}>{tg("button.cancel")}</Button>
                    <Button color={"red"} onClick={async () => await deletePlugin()}>
                        {t("button.delete")}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
